import { useTenantId } from '@/stores/tenant.store';
import { notifyDispatchUpdate } from '@/hooks/scheduler/useDispatchQueueSync';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerBooking } from '@/types/scheduler';

interface UnassignBookingDto {
  bookingId: string;
  technicianId?: string;
}

/**
 * Dispatch-board drag-to-unassign (§5/§6.6): drop an appointment on the
 * Unassigned row to free its tech + slot and return it to the queue. Errors
 * are surfaced to the caller so the board can revert the drag.
 */
export function useUnassignBooking() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useApiMutation<SchedulerBooking, UnassignBookingDto>({
    mutationFn: ({ bookingId, technicianId }) =>
      apiPost<SchedulerBooking, UnassignBookingDto>(
        operationsClient,
        schedulerEndpoints.dispatchUnassign(),
        { bookingId, ...(technicianId ? { technicianId } : {}) },
      ),
    showErrorToast: false,
    onSuccess: () => {
      notifyDispatchUpdate(tenantId);
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
    },
  });
}
